import React, { useState } from "react";
import { useReactiveVar } from "@apollo/client";
import { DateTime } from "luxon";
import { CompanyAccess, PermissionScope } from "@me-team/host/main/graphql/types";
import { currentBranchEmployeeIdVar } from "@me-team/host/main/globalVar/state";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

interface WorkScheduleAndLogbookResult {
    refetchEmployeeList: boolean;
    handleRefetchEmployeeList: () => void;
    handleClickAddVacation: (e: any) => void;
    handleWeekCellClick: (e: any) => void;
    handleCustomDay: (e: any) => void;
    handleCloseVacation: () => void;
    handleCustomModalClose: () => void;
    modalData: { show: boolean; employeeId: number | null; edit: boolean | null };
    setModalData: React.Dispatch<
        React.SetStateAction<{ show: boolean; employeeId: number | null; edit: boolean | null }>
    >;
    editDate: { startDate: DateTime | null };
    cancelModalData: {
        show: boolean;
        date: DateTime | null;
        resource: string | null;
        employeeId: number | null;
        edit: boolean | null;
    };
    setCancelModalData: React.Dispatch<
        React.SetStateAction<{
            show: boolean;
            employeeId: number | null;
            date: DateTime | null;
            edit: boolean | null;
            resource: string | null;
        }>
    >;
    customModalData: { show: boolean; date: any | null; resource: string | null };
    handleCancelWorkDay: (e: any) => void;
}

export const useWorkScheduleAndLogbook = (): WorkScheduleAndLogbookResult => {
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const canCreateVacationAll = useRoleAccess(CompanyAccess.VacationCreate, { scope: PermissionScope.All });
    const canCreateVacationSelf = useRoleAccess(CompanyAccess.VacationCreate, { scope: PermissionScope.Self });
    const canCreateCustomDayAll = useRoleAccess(CompanyAccess.CustomDayCreate, { scope: PermissionScope.All });
    const canCreateCustomDaySelf = useRoleAccess(CompanyAccess.CustomDayCreate, { scope: PermissionScope.Self });
    const [refetchEmployeeList, setRefetchEmployeeList] = useState<boolean>(false);
    const [modalData, setModalData] = useState({ show: false, employeeId: null, edit: null });
    const [customModalData, setCustomModalData] = useState({ show: false, date: null, resource: null });
    const [cancelModalData, setCancelModalData] = useState({
        show: false,
        date: null,
        resource: null,
        employeeId: null,
        edit: null,
    });
    const [editDate, setEditDate] = useState({ startDate: null });
    const handleRefetchEmployeeList = () => {
        setRefetchEmployeeList((prevRefetch) => !prevRefetch);
    };
    const extractEmployeeIdFromUrl = (url: string): number | null => {
        const match = url.match(/\/ajax\/schedule(?:\/\d+)*\/(?:new\/custom\/day|create)\/(\d+)/);
        return match ? parseInt(match[1], 10) : null;
    };
    const canAccessOwnEmployee = (employeeId: number | null) =>
        Boolean(currentBranchEmployeeId && employeeId && currentBranchEmployeeId === employeeId);

    const handleClickAddVacation = (event: any) => {
        const target = event.target.closest('[data-action="click->ajax-modal-get-data#loadModalContent"]');

        if (target) {
            event.preventDefault();
            const url = target.getAttribute("data-url");
            const employeeId = extractEmployeeIdFromUrl(url);
            const isOwnVacationEmployee = canAccessOwnEmployee(employeeId);
            const canCreateVacationForSelectedEmployee = isOwnVacationEmployee
                ? canCreateVacationSelf || canCreateVacationAll
                : canCreateVacationAll;

            if (!canCreateVacationForSelectedEmployee) return;
            setModalData({ show: true, employeeId: employeeId, edit: false });
        }
    };

    const handleCustomDay = (event: any) => {
        const date = event.detail.event.startDate;
        const resource = event.detail.event.responseObject;
        if (resource.color === "#EEECF9") {
            setEditDate({
                startDate: event.detail.event.responseObject.start.substring(0, 10),
            });
            setModalData({ show: true, employeeId: event.detail.event.resourceId, edit: true });
            handleClickAddVacation(event);
        } else {
            setCustomModalData({ show: true, date, resource });
        }
    };

    const handleCancelWorkDay = (event: any) => {
        const { date, resource } = event.detail;
        setCancelModalData({ show: true, date, resource, employeeId: resource.id, edit: null });
    };

    const handleWeekCellClick = (event: any) => {
        const { date, resource } = event.detail;
        const employeeId = resource?.id || resource?.resource || null;
        const isOwnCustomDayEmployee = canAccessOwnEmployee(employeeId);
        const canCreateCustomDayForSelectedEmployee = isOwnCustomDayEmployee
            ? canCreateCustomDaySelf || canCreateCustomDayAll
            : canCreateCustomDayAll;

        if (!canCreateCustomDayForSelectedEmployee) return;
        setCustomModalData({ show: true, date, resource });
    };

    const handleCloseVacation = () => {
        setModalData({ show: false, employeeId: null, edit: null });
    };
    const handleCustomModalClose = () => {
        setCustomModalData({ show: false, date: null, resource: null });
    };

    return {
        refetchEmployeeList,
        handleRefetchEmployeeList,
        handleClickAddVacation,
        handleWeekCellClick,
        handleCustomDay,
        modalData,
        handleCloseVacation,
        handleCustomModalClose,
        editDate,
        cancelModalData,
        setCancelModalData,
        customModalData,
        handleCancelWorkDay,
        setModalData,
    };
};
